import { FormFieldObject, GlossaryItem, sectionDataObject } from "../stores/objectsStore";
import { GlossaryBlueprint } from "../../types-games";
import { parseBlueprintData, FormField, validateFormData } from "./blueprintDataParser";
import { getCachedStringsItems } from "@/lib/stores/stringsStore";
import { Version } from "../stores/storeTypes";

/**
 * Get the appropriate default value for a field based on its type
 */
function getDefaultValueForFieldType(type: string, providedDefault?: any): any {
  // If a default value was explicitly provided, use it
  if (providedDefault !== undefined) {
    return providedDefault;
  }
  
  // Otherwise, return appropriate default based on type
  switch (type) {
    case 'boolean':
      return false;
    case 'number':
      return 0;
    case 'tags':
    case 'types':
      return [];
    case 'glossaryItem':
      return '';
    default:
      return '';
  }
}

/**
 * Convert legacy field values to new TranslatableValue format for translatable multiple fields
 * This ensures backwards compatibility with existing data
 */
function convertToTranslatableValueFormat(field: any): any {
  // Only process translatable multiple fields
  if (!field.translation || !field.multiple) {
    return field;
  }
  
  const value = field.value;
  
  // If value is not an array, nothing to convert
  if (!Array.isArray(value)) {
    return field;
  }
  
  // Check if already in new format (array of TranslatableValue objects)
  const isNewFormat = value.every(
    (item: any) => item && typeof item === 'object' && 'text' in item
  );
  
  if (isNewFormat) {
    return field; // Already in new format
  }
  
  // Convert from old format (array of strings) to new format (array of TranslatableValue)
  const convertedValue = value.map((item: any) => {
    if (typeof item === 'string') {
      return { text: item, string_id: undefined };
    } else if (item && typeof item === 'object' && 'text' in item) {
      return item; // Already in correct format
    } else {
      return { text: String(item || ''), string_id: undefined };
    }
  });
  
  return {
    ...field,
    value: convertedValue
  };
}

export interface ParsedGlossaryItemData {
  bluePrintFields: FormField[];
  bluePrintSections: Array<{ id: string; title: string; fieldIds: string[] }>;
  formData: Record<string, any>;
  metadata?: {
    title?: string;
    description?: string;
    version?: number;
    blueprintId?: string;
  };
  sections: sectionDataObject[];
  validation: {
    isValid: boolean;
    errors: Record<string, string>;
  };
}



/**
 * Parse glossary item data to extract form fields and current form data
 * This utility ensures consistent parsing of glossary items for display and editing
 */
export function parseGlossaryItemData(
  glossaryItem: ObjectItem,
  blueprint?: GlossaryBlueprint
): ParsedGlossaryItemData {
  const bluePrintFields: FormField[] = [];
  const bluePrintSections: Array<{ id: string; title: string; fieldIds: string[] }> = [];
  const fields: FormFieldObject[] = [];
  const sections: sectionDataObject[] = [];
  const formData: Record<string, any> = {};

  // If blueprint is provided, use it to get field definitions and order information
  if (blueprint) {
    const parsedBlueprint = parseBlueprintData(blueprint);
    bluePrintFields.push(...parsedBlueprint.fields);
    if (parsedBlueprint.sections) {
      bluePrintSections.push(...parsedBlueprint.sections);
    }
  }

  // Parse the actual data from the glossary item
  if (glossaryItem.data && typeof glossaryItem.data === 'object') {
    // Convert legacy translatable multiple field values to new format
    const convertedData = Array.isArray(glossaryItem.data) 
      ? glossaryItem.data.map(section => ({
          ...section,
          fields: section.fields.map(convertToTranslatableValueFormat)
        }))
      : glossaryItem.data;
    const itemData = convertedData;

    // Convert itemData to array format if it's an object
    let itemDataArray: sectionDataObject[] = [];
    if (Array.isArray(itemData)) {
      itemDataArray = itemData;
    } else if (typeof itemData === 'object' && itemData !== null) {
      // Convert object with numeric keys to array
      itemDataArray = Object.values(itemData);
    }

    // Create a map of sections by ID for quick lookup
    const sectionsMap = new Map<string, sectionDataObject>();
    itemDataArray.forEach((section: sectionDataObject) => {
      sectionsMap.set(section.id, section);
    });

    // If blueprint is provided, order sections and fields according to blueprint
    if (blueprint && blueprint.data && typeof blueprint.data === 'object') {
      const blueprintData = blueprint.data as Record<string, any>;

      // Check if elementOrder exists for section ordering
      if (blueprintData.elementOrder && Array.isArray(blueprintData.elementOrder)) {
        // Use blueprint's elementOrder to sort sections
        blueprintData.elementOrder.forEach((elementId: string) => {
          const section = sectionsMap.get(elementId);
          if (section) {
            // Order fields within the section according to blueprint
            const orderedFields = orderFieldsInSection(section, blueprintData.sections?.[elementId]);

            // Add fields to formData
            orderedFields.forEach((field) => {
              formData[field.id] = field.value;
            });

            // Add ordered section to sections array
            sections.push({
              id: section.id,
              title: section.title,
              fields: orderedFields,
              documentation: blueprintData.sections?.[elementId]?._sectionDocumentation, // Include section documentation from blueprint
              hiddenForRoles: blueprintData.sections?.[elementId]?.hiddenForRoles || [], // Include section role visibility from blueprint
            });

            // Remove from map to avoid duplicates
            sectionsMap.delete(elementId);
          }
        });
      }

      // Add any remaining sections that weren't in elementOrder
      sectionsMap.forEach((section) => {
        const orderedFields = orderFieldsInSection(section, blueprintData.sections?.[section.id]);

        // Add fields to formData
        orderedFields.forEach((field) => {
          formData[field.id] = field.value;
        });

        // Add section to sections array
        sections.push({
          id: section.id,
          title: section.title,
          fields: orderedFields,
          documentation: blueprintData.sections?.[section.id]?._sectionDocumentation, // Include section documentation from blueprint
          hiddenForRoles: blueprintData.sections?.[section.id]?.hiddenForRoles || [], // Include section role visibility from blueprint
        });
      });
    } else {
      // No blueprint or no elementOrder - use original order
      itemDataArray.forEach((section: sectionDataObject) => {
        // Add fields to formData
        section.fields.forEach((field) => {
          formData[field.id] = field.value;
        });

        // Add section to sections array
        sections.push({
          id: section.id,
          title: section.title,
          fields: section.fields.map((f) => f),
        });
      });
    }
  }

  // Validate the form data against field requirements
  const validation = validateFormData(formData, fields);

  // Extract metadata
  const metadata = {
    title: glossaryItem.name,
    version: glossaryItem.version?.version,
    blueprintId: glossaryItem.blueprint_id || undefined,
  };

  return {
    bluePrintFields,
    bluePrintSections,
    formData,
    metadata,
    sections,
    validation,
  };
}

// Helper function to order fields within a section according to blueprint
function orderFieldsInSection(
  section: sectionDataObject,
  blueprintSection?: Record<string, any>
): FormFieldObject[] {
  if (!blueprintSection || !blueprintSection._fieldOrder) {
    return section.fields;
  }

  const fieldOrder = blueprintSection._fieldOrder as string[];
  const fieldsMap = new Map(section.fields.map(field => [field.id, field]));
  const orderedFields: FormFieldObject[] = [];

  // Add fields in the order specified by blueprint
  fieldOrder.forEach((fieldId) => {
    const field = fieldsMap.get(fieldId);
    if (field) {
      // Merge field documentation and role visibility from blueprint if available
      const fieldConfig = blueprintSection[fieldId];
      if (fieldConfig) {
        orderedFields.push({
          ...field,
          documentation: fieldConfig.documentation, // Add field documentation from blueprint
          hiddenForRoles: fieldConfig.hiddenForRoles || [], // Add field role visibility from blueprint
          readOnlyForRoles: fieldConfig.readOnlyForRoles || [], // Add field readonly visibility from blueprint
        });
      } else {
        orderedFields.push(field);
      }
      fieldsMap.delete(fieldId); // Remove to avoid duplicates
    }
  });

  // Add any remaining fields that weren't in fieldOrder
  fieldsMap.forEach((field) => {
    orderedFields.push(field);
  });

  return orderedFields;
}

/**
 * Rebuild glossary item structure from current blueprint, preserving existing values
 * This ensures the structure always matches the latest blueprint when editing
 */
export function rebuildGlossaryItemFromBlueprint(
  blueprint: GlossaryBlueprint,
  existingGlossaryItem: ObjectItem
): {
  sections: sectionDataObject[];
  formData: Record<string, any>;
} {
  const sections: sectionDataObject[] = [];
  const formData: Record<string, any> = {};

  if (!blueprint.data || typeof blueprint.data !== 'object') {
    return { sections, formData };
  }

  // Build a map of existing field values by field ID
  const existingFieldValues = new Map<string, any>();
  const existingStringIds = new Map<string, string>();
  // For multiple translatable fields, store string_ids by field_id and index
  const existingArrayStringIds = new Map<string, Map<number, string>>();
  
  if (existingGlossaryItem.data && Array.isArray(existingGlossaryItem.data)) {
    existingGlossaryItem.data.forEach((section: sectionDataObject) => {
      section.fields.forEach((field) => {
        existingFieldValues.set(field.id, field.value);
        if (field.string_id) {
          existingStringIds.set(field.id, field.string_id);
        }
        // For multiple translatable fields, extract string_ids from array items
        if (field.multiple && field.translation && Array.isArray(field.value)) {
          const indexMap = new Map<number, string>();
          field.value.forEach((item: any, index: number) => {
            if (item && typeof item === 'object' && item.string_id) {
              indexMap.set(index, item.string_id);
            }
          });
          if (indexMap.size > 0) {
            existingArrayStringIds.set(field.id, indexMap);
          }
        }
      });
    });
  }

  console.log(`[RebuildGlossaryItem] Found ${existingFieldValues.size} existing field values, ${existingArrayStringIds.size} fields with array string_ids`);

  const blueprintData = blueprint.data as Record<string, any>;
  const blueprintSections = blueprintData.sections;

  if (!blueprintSections || typeof blueprintSections !== 'object') {
    return { sections, formData };
  }

  // Create a map of sections by ID for quick lookup
  const sectionsMap = new Map<string, any>();
  Object.entries(blueprintSections).forEach(([sectionKey, sectionConfig]) => {
    if (typeof sectionConfig === 'object') {
      sectionsMap.set(sectionKey, sectionConfig);
    }
  });

  // If elementOrder exists, use it to order sections
  if (blueprintData.elementOrder && Array.isArray(blueprintData.elementOrder)) {
    blueprintData.elementOrder.forEach((elementId: string) => {
      const sectionConfig = sectionsMap.get(elementId);
      if (sectionConfig) {
        const orderedFields = buildFieldsFromBlueprintWithExistingData(
          sectionConfig, 
          elementId,
          existingFieldValues,
          existingStringIds,
          existingArrayStringIds
        );
        
        // Add fields to formData
        orderedFields.forEach((field) => {
          formData[field.id] = field.value;
        });

        // Add ordered section to sections array
        sections.push({
          id: elementId,
          title: sectionConfig._sectionTitle || elementId,
          fields: orderedFields,
          documentation: sectionConfig._sectionDocumentation, // Add section documentation from blueprint
          hiddenForRoles: sectionConfig.hiddenForRoles || [], // Add section role visibility from blueprint
          readOnlyForRoles: sectionConfig.readOnlyForRoles || [], // Add section readonly visibility from blueprint
        });

        // Remove from map to avoid duplicates
        sectionsMap.delete(elementId);
      }
    });
  }

  // Add any remaining sections that weren't in elementOrder
  sectionsMap.forEach((sectionConfig, sectionKey) => {
    const orderedFields = buildFieldsFromBlueprintWithExistingData(
      sectionConfig,
      sectionKey,
      existingFieldValues,
      existingStringIds,
      existingArrayStringIds
    );
    
    // Add fields to formData
    orderedFields.forEach((field) => {
      formData[field.id] = field.value;
    });

    // Add section to sections array
    sections.push({
      id: sectionKey,
      title: sectionConfig._sectionTitle || sectionKey,
      fields: orderedFields,
      documentation: sectionConfig._sectionDocumentation, // Add section documentation from blueprint
      hiddenForRoles: sectionConfig.hiddenForRoles || [], // Add section role visibility from blueprint
      readOnlyForRoles: sectionConfig.readOnlyForRoles || [], // Add section readonly visibility from blueprint
    });
  });

  console.log(`[RebuildGlossaryItem] Built ${sections.length} sections from blueprint`);
  return { sections, formData };
}

/**
 * Create a new glossary item structure from blueprint with proper ordering
 * This function ensures that sections and fields are ordered according to the blueprint's elementOrder and _fieldOrder
 */
export function createNewGlossaryItemFromBlueprint(
  blueprint: GlossaryBlueprint
): {
  sections: sectionDataObject[];
  formData: Record<string, any>;
} {
  const sections: sectionDataObject[] = [];
  const formData: Record<string, any> = {};

  if (!blueprint.data || typeof blueprint.data !== 'object') {
    return { sections, formData };
  }

  const blueprintData = blueprint.data as Record<string, any>;
  const blueprintSections = blueprintData.sections;

  if (!blueprintSections || typeof blueprintSections !== 'object') {
    return { sections, formData };
  }

  // Create a map of sections by ID for quick lookup
  const sectionsMap = new Map<string, any>();
  Object.entries(blueprintSections).forEach(([sectionKey, sectionConfig]) => {
    if (typeof sectionConfig === 'object') {
      sectionsMap.set(sectionKey, sectionConfig);
    }
  });

  // If elementOrder exists, use it to order sections
  if (blueprintData.elementOrder && Array.isArray(blueprintData.elementOrder)) {
    blueprintData.elementOrder.forEach((elementId: string) => {
      const sectionConfig = sectionsMap.get(elementId);
      if (sectionConfig) {
        const orderedFields = orderFieldsInSectionForNewItem(sectionConfig, elementId);
        
        // Add fields to formData
        orderedFields.forEach((field) => {
          formData[field.id] = field.value;
        });

        // Add ordered section to sections array
        sections.push({
          id: elementId,
          title: sectionConfig._sectionTitle || elementId,
          fields: orderedFields,
          documentation: sectionConfig._sectionDocumentation, // Add section documentation from blueprint
          hiddenForRoles: sectionConfig.hiddenForRoles || [], // Add section role visibility from blueprint
          readOnlyForRoles: sectionConfig.readOnlyForRoles || [], // Add section readonly visibility from blueprint
        });

        // Remove from map to avoid duplicates
        sectionsMap.delete(elementId);
      }
    });
  }

  // Add any remaining sections that weren't in elementOrder
  sectionsMap.forEach((sectionConfig, sectionKey) => {
    const orderedFields = orderFieldsInSectionForNewItem(sectionConfig, sectionKey);
    
    // Add fields to formData
    orderedFields.forEach((field) => {
      formData[field.id] = field.value;
    });

    // Add section to sections array
    sections.push({
      id: sectionKey,
      title: sectionConfig._sectionTitle || sectionKey,
      fields: orderedFields,
      documentation: sectionConfig._sectionDocumentation, // Add section documentation from blueprint
      hiddenForRoles: sectionConfig.hiddenForRoles || [], // Add section role visibility from blueprint
      readOnlyForRoles: sectionConfig.readOnlyForRoles || [], // Add section readonly visibility from blueprint
    });
  });

  return { sections, formData };
}

/**
 * Build fields from blueprint section config, filling in existing values where available
 */
function buildFieldsFromBlueprintWithExistingData(
  sectionConfig: Record<string, any>,
  sectionKey: string,
  existingFieldValues: Map<string, any>,
  existingStringIds: Map<string, string>,
  existingArrayStringIds: Map<string, Map<number, string>>
): any[] {
  const fields: any[] = [];
  const fieldOrder = sectionConfig._fieldOrder;

  // Check if this is a single field section (has direct type property)
  if (sectionConfig.type) {
    // Single field section
    const fieldId = sectionConfig.id || `${sectionKey}_field`;
    const existingValue = existingFieldValues.get(fieldId);
    const existingStringId = existingStringIds.get(fieldId);
    const existingArrayIds = existingArrayStringIds.get(fieldId);
    
    // Get default value
    let defaultValue = existingValue !== undefined 
      ? existingValue 
      : getDefaultValueForFieldType(sectionConfig.type, sectionConfig.defaultValue);
    
    // For translatable multiple fields, ensure value is in TranslatableValue format
    // and preserve existing string_ids
    const isTranslatableMultiple = (sectionConfig.translation || sectionConfig.trans) && sectionConfig.multiple;
    if (isTranslatableMultiple && Array.isArray(defaultValue)) {
      defaultValue = defaultValue.map((item: any, index: number) => {
        // Get existing string_id for this index if available
        const existingIdForIndex = existingArrayIds?.get(index);
        
        if (typeof item === 'string') {
          // Plain string - convert to TranslatableValue, preserving string_id if available
          return { text: item, string_id: existingIdForIndex };
        } else if (item && typeof item === 'object' && 'text' in item) {
          // Already in TranslatableValue format - preserve existing string_id or use from map
          return { 
            text: item.text, 
            string_id: item.string_id || existingIdForIndex 
          };
        } else {
          return { text: '', string_id: existingIdForIndex };
        }
      });
    }
    
    const field: any = {
      id: fieldId,
      type: sectionConfig.type,
      label: sectionConfig.label || sectionConfig._sectionTitle || sectionKey,
      value: defaultValue,
      required: sectionConfig.required || sectionConfig.req || false,
      placeholder: sectionConfig.placeholder || `Enter ${sectionConfig.label || sectionConfig._sectionTitle || sectionKey}`,
      options: sectionConfig.options,
      accept: sectionConfig.accept,
      multiple: sectionConfig.multiple || false,
      validation: sectionConfig.validation,
      translation: sectionConfig.translation || sectionConfig.trans || false,
      filter: sectionConfig.filter || false,
      filterOptions: sectionConfig.filterOptions || [],
      documentation: sectionConfig.documentation, // Add field documentation from blueprint
      hiddenForRoles: sectionConfig.hiddenForRoles || [], // Add field role visibility from blueprint
      readOnlyForRoles: sectionConfig.readOnlyForRoles || [], // Add field readonly visibility from blueprint
    };
    
    // Preserve string_id if it exists (for backwards compatibility with single translatable fields)
    if (existingStringId && !isTranslatableMultiple) {
      field.string_id = existingStringId;
    }
    
    fields.push(field);
  } else {
    // Multi-field section
    if (fieldOrder && Array.isArray(fieldOrder)) {
      // Use the stored field order
      fieldOrder.forEach((fieldKey: string) => {
        const fieldConfig = sectionConfig[fieldKey];
        if (typeof fieldConfig === 'object' && fieldConfig.type) {
          const fieldId = fieldConfig.id || `${sectionKey}_${fieldKey}`;
          const existingValue = existingFieldValues.get(fieldId);
          const existingStringId = existingStringIds.get(fieldId);
          const existingArrayIds = existingArrayStringIds.get(fieldId);
          
          // Get default value
          let defaultValue = existingValue !== undefined 
            ? existingValue 
            : getDefaultValueForFieldType(fieldConfig.type, fieldConfig.defaultValue);
          
          // For translatable multiple fields, ensure value is in TranslatableValue format
          // and preserve existing string_ids
          const isTranslatableMultiple = (fieldConfig.translation || fieldConfig.trans) && fieldConfig.multiple;
          if (isTranslatableMultiple && Array.isArray(defaultValue)) {
            defaultValue = defaultValue.map((item: any, index: number) => {
              // Get existing string_id for this index if available
              const existingIdForIndex = existingArrayIds?.get(index);
              
              if (typeof item === 'string') {
                // Plain string - convert to TranslatableValue, preserving string_id if available
                return { text: item, string_id: existingIdForIndex };
              } else if (item && typeof item === 'object' && 'text' in item) {
                // Already in TranslatableValue format - preserve existing string_id or use from map
                return { 
                  text: item.text, 
                  string_id: item.string_id || existingIdForIndex 
                };
              } else {
                return { text: '', string_id: existingIdForIndex };
              }
            });
          }
          
          const field: any = {
            id: fieldId,
            type: fieldConfig.type,
            label: fieldConfig.label || fieldKey,
            value: defaultValue,
            required: fieldConfig.required || fieldConfig.req || false,
            placeholder: fieldConfig.placeholder || `Enter ${fieldConfig.label || fieldKey}`,
            options: fieldConfig.options,
            accept: fieldConfig.accept,
            multiple: fieldConfig.multiple || false,
            validation: fieldConfig.validation,
            translation: fieldConfig.translation || fieldConfig.trans || false,
            filter: fieldConfig.filter || false,
            filterOptions: fieldConfig.filterOptions || [],
            documentation: fieldConfig.documentation, // Add field documentation from blueprint
            hiddenForRoles: fieldConfig.hiddenForRoles || [], // Add field role visibility from blueprint
            readOnlyForRoles: fieldConfig.readOnlyForRoles || [], // Add field readonly visibility from blueprint
          };
          
          // Preserve string_id if it exists (for backwards compatibility with single translatable fields)
          if (existingStringId && !isTranslatableMultiple) {
            field.string_id = existingStringId;
          }
          
          fields.push(field);
        }
      });
    } else {
      // No field order specified, process fields in object order
      Object.entries(sectionConfig).forEach(([fieldKey, fieldConfig]) => {
        // Skip the _fieldOrder and _sectionTitle properties
        if (fieldKey === '_fieldOrder' || fieldKey === '_sectionTitle') return;

        if (typeof fieldConfig === 'object' && fieldConfig.type) {
          const fieldId = fieldConfig.id || `${sectionKey}_${fieldKey}`;
          const existingValue = existingFieldValues.get(fieldId);
          const existingStringId = existingStringIds.get(fieldId);
          const existingArrayIds = existingArrayStringIds.get(fieldId);
          
          // Get default value
          let defaultValue = existingValue !== undefined 
            ? existingValue 
            : getDefaultValueForFieldType(fieldConfig.type, fieldConfig.defaultValue);
          
          // For translatable multiple fields, ensure value is in TranslatableValue format
          // and preserve existing string_ids
          const isTranslatableMultiple = (fieldConfig.translation || fieldConfig.trans) && fieldConfig.multiple;
          if (isTranslatableMultiple && Array.isArray(defaultValue)) {
            defaultValue = defaultValue.map((item: any, index: number) => {
              // Get existing string_id for this index if available
              const existingIdForIndex = existingArrayIds?.get(index);
              
              if (typeof item === 'string') {
                // Plain string - convert to TranslatableValue, preserving string_id if available
                return { text: item, string_id: existingIdForIndex };
              } else if (item && typeof item === 'object' && 'text' in item) {
                // Already in TranslatableValue format - preserve existing string_id or use from map
                return { 
                  text: item.text, 
                  string_id: item.string_id || existingIdForIndex 
                };
              } else {
                return { text: '', string_id: existingIdForIndex };
              }
            });
          }
          
          const field: any = {
            id: fieldId,
            type: fieldConfig.type,
            label: fieldConfig.label || fieldKey,
            value: defaultValue,
            required: fieldConfig.required || fieldConfig.req || false,
            placeholder: fieldConfig.placeholder || `Enter ${fieldConfig.label || fieldKey}`,
            options: fieldConfig.options,
            accept: fieldConfig.accept,
            multiple: fieldConfig.multiple || false,
            validation: fieldConfig.validation,
            translation: fieldConfig.translation || fieldConfig.trans || false,
            filter: fieldConfig.filter || false,
            filterOptions: fieldConfig.filterOptions || [],
            documentation: fieldConfig.documentation, // Add field documentation from blueprint
            hiddenForRoles: fieldConfig.hiddenForRoles || [], // Add field role visibility from blueprint
            readOnlyForRoles: fieldConfig.readOnlyForRoles || [], // Add field readonly visibility from blueprint
          };
          
          // Preserve string_id if it exists (for backwards compatibility with single translatable fields)
          if (existingStringId && !isTranslatableMultiple) {
            field.string_id = existingStringId;
          }
          
          fields.push(field);
        }
      });
    }
  }

  return fields;
}

/**
 * Helper function to order fields within a section for new items according to blueprint
 */
function orderFieldsInSectionForNewItem(
  sectionConfig: Record<string, any>,
  sectionKey: string
): any[] {
  const fields: any[] = [];
  const fieldOrder = sectionConfig._fieldOrder;

  // Check if this is a single field section (has direct type property)
  if (sectionConfig.type) {
    // Single field section
    // Get default value
    let defaultValue = getDefaultValueForFieldType(sectionConfig.type, sectionConfig.defaultValue);
    
    // For translatable multiple fields, ensure value starts in TranslatableValue format
    const isTranslatableMultiple = (sectionConfig.translation || sectionConfig.trans) && sectionConfig.multiple;
    if (isTranslatableMultiple) {
      defaultValue = [{ text: '', string_id: undefined }];
    }
    
    const field = {
      id: sectionConfig.id || `${sectionKey}_field`,
      type: sectionConfig.type,
      label: sectionConfig.label || sectionConfig._sectionTitle || sectionKey,
      value: defaultValue,
      required: sectionConfig.required || sectionConfig.req || false,
      placeholder: sectionConfig.placeholder || `Enter ${sectionConfig.label || sectionConfig._sectionTitle || sectionKey}`,
      options: sectionConfig.options,
      accept: sectionConfig.accept,
      multiple: sectionConfig.multiple || false,
      validation: sectionConfig.validation,
      translation: sectionConfig.translation || sectionConfig.trans || false,
      filter: sectionConfig.filter || false,
      filterOptions: sectionConfig.filterOptions || [],
      documentation: sectionConfig.documentation, // Add field documentation from blueprint
      hiddenForRoles: sectionConfig.hiddenForRoles || [], // Add field role visibility from blueprint
      readOnlyForRoles: sectionConfig.readOnlyForRoles || [], // Add field readonly visibility from blueprint
    };
    fields.push(field);
  } else {
    // Multi-field section
    if (fieldOrder && Array.isArray(fieldOrder)) {
      // Use the stored field order
      fieldOrder.forEach((fieldKey: string) => {
        const fieldConfig = sectionConfig[fieldKey];
        if (typeof fieldConfig === 'object' && fieldConfig.type) {
          // Get default value
          let defaultValue = getDefaultValueForFieldType(fieldConfig.type, fieldConfig.defaultValue);
          
          // For translatable multiple fields, ensure value starts in TranslatableValue format
          const isTranslatableMultiple = (fieldConfig.translation || fieldConfig.trans) && fieldConfig.multiple;
          if (isTranslatableMultiple) {
            defaultValue = [{ text: '', string_id: undefined }];
          }
          
          const field = {
            id: fieldConfig.id || `${sectionKey}_${fieldKey}`,
            type: fieldConfig.type,
            label: fieldConfig.label || fieldKey,
            value: defaultValue,
            required: fieldConfig.required || fieldConfig.req || false,
            placeholder: fieldConfig.placeholder || `Enter ${fieldConfig.label || fieldKey}`,
            options: fieldConfig.options,
            accept: fieldConfig.accept,
            multiple: fieldConfig.multiple || false,
            validation: fieldConfig.validation,
            translation: fieldConfig.translation || fieldConfig.trans || false,
            filter: fieldConfig.filter || false,
            filterOptions: fieldConfig.filterOptions || [],
            documentation: fieldConfig.documentation, // Add field documentation from blueprint
            hiddenForRoles: fieldConfig.hiddenForRoles || [], // Add field role visibility from blueprint
            readOnlyForRoles: fieldConfig.readOnlyForRoles || [], // Add field readonly visibility from blueprint
          };
          fields.push(field);
        }
      });
    } else {
      // No field order specified, process fields in object order
      Object.entries(sectionConfig).forEach(([fieldKey, fieldConfig]) => {
        // Skip the _fieldOrder property
        if (fieldKey === '_fieldOrder') return;

        if (typeof fieldConfig === 'object' && fieldConfig.type) {
          // Get default value
          let defaultValue = getDefaultValueForFieldType(fieldConfig.type, fieldConfig.defaultValue);
          
          // For translatable multiple fields, ensure value starts in TranslatableValue format
          const isTranslatableMultiple = (fieldConfig.translation || fieldConfig.trans) && fieldConfig.multiple;
          if (isTranslatableMultiple) {
            defaultValue = [{ text: '', string_id: undefined }];
          }
          
          const field = {
            id: fieldConfig.id || `${sectionKey}_${fieldKey}`,
            type: fieldConfig.type,
            label: fieldConfig.label || fieldKey,
            value: defaultValue,
            required: fieldConfig.required || fieldConfig.req || false,
            placeholder: fieldConfig.placeholder || `Enter ${fieldConfig.label || fieldKey}`,
            options: fieldConfig.options,
            accept: fieldConfig.accept,
            multiple: fieldConfig.multiple || false,
            validation: fieldConfig.validation,
            translation: fieldConfig.translation || fieldConfig.trans || false,
            filter: fieldConfig.filter || false,
            filterOptions: fieldConfig.filterOptions || [],
            documentation: fieldConfig.documentation, // Add field documentation from blueprint
            hiddenForRoles: fieldConfig.hiddenForRoles || [], // Add field role visibility from blueprint
            readOnlyForRoles: fieldConfig.readOnlyForRoles || [], // Add field readonly visibility from blueprint
          };
          fields.push(field);
        }
      });
    }
  }

  return fields;
}

/**
 * Validate glossary item data against its blueprint requirements
 */
export function validateGlossaryItemData(
  glossaryItem: ObjectItem,
  blueprint: GlossaryBlueprint
): { isValid: boolean; errors: Record<string, string> } {
  const parsedData = parseGlossaryItemData(glossaryItem, blueprint);
  return parsedData.validation;
}

/**
 * Get field values for a specific field in a glossary item
 * Handles both single and multiple field values
 */
export function getGlossaryItemFieldValue(
  glossaryItem: ObjectItem,
  fieldId: string
): any {
  if (!glossaryItem.data || typeof glossaryItem.data !== 'object') {
    return undefined;
  }

  const itemData = glossaryItem.data as Record<string, any>;

  // Check if field exists in flat structure
  if (itemData[fieldId] !== undefined) {
    return itemData[fieldId];
  }

  // Check if field exists in sections structure
  if (itemData.sections && typeof itemData.sections === 'object') {
    Object.entries(itemData.sections).forEach(([sectionKey, sectionData]) => {
      if (typeof sectionData === 'object') {
        const sectionFieldId = `${sectionKey}_${fieldId}`;
        if (itemData[sectionFieldId] !== undefined) {
          return itemData[sectionFieldId];
        }
      }
    });
  }

  return undefined;
}

/**
 * Update a specific field value in glossary item data
 * Handles both single and multiple field values
 */
export function updateGlossaryItemFieldValue(
  glossaryItem: ObjectItem,
  fieldId: string,
  value: any
): ObjectItem {
  const updatedData = { ...glossaryItem.data } as any;

  // Check if field exists in flat structure
  if (updatedData[fieldId] !== undefined) {
    updatedData[fieldId] = value;
  } else if (updatedData.sections && typeof updatedData.sections === 'object') {
    // Check if field exists in sections structure
    Object.entries(updatedData.sections).forEach(([sectionKey, sectionData]) => {
      if (typeof sectionData === 'object') {
        const sectionFieldId = `${sectionKey}_${fieldId}`;
        if (updatedData[sectionFieldId] !== undefined) {
          updatedData[sectionFieldId] = value;
        }
      }
    });
  }

  return {
    ...glossaryItem,
    data: updatedData,
    updated_at: new Date().toISOString(),
  };
}

/**
 * Check if a glossary item has multiple values for a specific field
 */
export function hasMultipleFieldValues(
  glossaryItem: ObjectItem,
  fieldId: string
): boolean {
  const value = getGlossaryItemFieldValue(glossaryItem, fieldId);
  return Array.isArray(value) && value.length > 1;
}

/**
 * Get all field values for a multiple field in a glossary item
 */
export function getMultipleFieldValues(
  glossaryItem: ObjectItem,
  fieldId: string
): any[] {
  const value = getGlossaryItemFieldValue(glossaryItem, fieldId);
  return Array.isArray(value) ? value : [value].filter(v => v !== undefined);
}

/**
 * Generate LocaKey (abbrev) for glossary items
 * Format: Glos_title of glossary_number of the key from that glossary
 * @param glossaryTitle - The title of the glossary item
 * @param fieldKey - The key/ID of the field
 * @returns Formatted LocaKey
 */
export function generateGlossaryLocaKey(glossaryTitle: string, fieldKey: string): string {
  // Clean the title: remove special characters and convert to lowercase
  const cleanTitle = glossaryTitle
    .replace(/[^a-zA-Z0-9\s]/g, '') // Remove special characters
    .replace(/\s+/g, '_') // Replace spaces with underscores
    .toLowerCase();

  // Format: Glos_title of glossary_number of the key from that glossary
  return `Glos_${cleanTitle}_${fieldKey}`;
}

/**
 * Process glossary item data to create strings for translatable fields
 * @param glossaryItem - The glossary item data
 * @param gameId - The game ID
 * @param createStringFn - Function to create strings
 * @returns Updated glossary item with string IDs
 */
export async function processObjectForTranslation(
  glossaryItem: {
    id?: string;
    game_id: string;
    name: string;
    type?: string;
    data: sectionDataObject[];
    tags?: string[];
    blueprint_id?: string;
    version?: Version;
    blueprint_version?: number | Version;
    identifiers?: string[];
    folder?: string | null;
    status?: string;
  },
  createStringFn: (stringData: any) => Promise<any>,
  updateStringFn?: (stringData: any) => Promise<any>
): Promise<{
  glossaryItem: any;
  createdStrings: any[];
  updatedStrings: any[];
}> {
  // Validate required fields before processing
  if (!glossaryItem.game_id) {
    console.error('[processObjectForTranslation] Missing game_id for item:', glossaryItem.id || 'new', glossaryItem);
    throw new Error(`Cannot process object for translation: missing game_id. Object ID: ${glossaryItem.id || 'new'}`);
  }
  
  const createdStrings: any[] = [];
  const updatedStrings: any[] = [];
  const updatedData = [...glossaryItem.data];
  
  // Track which fields we're creating strings for to avoid update conflicts
  const fieldsToCreateStrings: string[] = [];
  
  console.log(`Processing glossary item ${glossaryItem.id || 'pending'} (game_id: ${glossaryItem.game_id}) for translation with ${glossaryItem.data.length} sections`);
  console.log("DEBUG - processObjectForTranslation input identifiers:", glossaryItem.identifiers);

  for (let sectionIndex = 0; sectionIndex < updatedData.length; sectionIndex++) {
    const section = updatedData[sectionIndex];
    
    for (let fieldIndex = 0; fieldIndex < section.fields.length; fieldIndex++) {
      const field = section.fields[fieldIndex];
      
      // Skip if field is not translatable or has no value
      if (!field.translation || !field.value) {
        continue;
      }
      
      // Handle multiple translatable fields (arrays of text)
      if (field.multiple && Array.isArray(field.value)) {
        console.log(`Processing multiple translatable field ${field.id} with ${field.value.length} values`);
        
        // Convert array values to TranslatableValue format
        const translatableValues: any[] = [];
        
        for (let valueIndex = 0; valueIndex < field.value.length; valueIndex++) {
          const value = field.value[valueIndex];
          
          // Handle different value formats
          let textValue: string;
          let existingStringId: string | undefined;
          
          if (typeof value === 'object' && value !== null && 'text' in value) {
            // Already in TranslatableValue format
            textValue = value.text || '';
            existingStringId = value.string_id;
          } else if (typeof value === 'string') {
            // Plain string value
            textValue = value;
            existingStringId = undefined;
          } else if (!value) {
            // Null or undefined value
            textValue = '';
            existingStringId = undefined;
          } else {
            // Unsupported format, skip
            translatableValues.push({ text: '', string_id: undefined });
            continue;
          }
          
          // Try to update existing string (even if empty)
          if (existingStringId && updateStringFn) {
            try {
              console.log(`Attempting to update existing string ${existingStringId} for field ${field.id}[${valueIndex}]`);
              await updateStringFn({
                id: existingStringId,
                game_id: glossaryItem.game_id,
                source_text: textValue
              });
              updatedStrings.push({ id: existingStringId, source_text: textValue });
              translatableValues.push({ text: textValue, string_id: existingStringId });
              console.log(`Successfully updated string ${existingStringId} for field ${field.id}[${valueIndex}]`);
              continue;
            } catch (error) {
              console.error(`Failed to update string for field ${field.id}[${valueIndex}]:`, error);
              existingStringId = undefined; // Will create new string below
            }
          }
          
          // Create new string even if empty (to establish the link)
          if (!existingStringId) {
            try {
              console.log(`Creating new string for field ${field.id}[${valueIndex}] with value: "${textValue}"`);
              
              // Generate LocaKey with index
              const locaKey = generateGlossaryLocaKey(glossaryItem.name, `${field.id}_${valueIndex}`);
              
              // Create string metadata
              const metadata = [
                { key: 'glossary_item_id', value: glossaryItem.id || 'pending' },
                { key: 'field_id', value: field.id },
                { key: 'field_index', value: String(valueIndex) },
                { key: 'field_label', value: field.label },
                { key: 'glossary_title', value: glossaryItem.name },
                { key: 'source', value: 'glossary_item' }
              ];
              
              // Create the string (even if empty to establish link)
              const newString = await createStringFn({
                game_id: glossaryItem.game_id,
                source_text: textValue,
                section: 'glossary',
                context: `Glossary item: ${glossaryItem.name} - Field: ${field.label} [${valueIndex}]`,
                metadata,
                abbrev: locaKey,
                status: 'pending'
              });
              
              translatableValues.push({ text: textValue, string_id: newString.id });
              createdStrings.push(newString);
              console.log(`Successfully created string ${newString.id} for field ${field.id}[${valueIndex}]`);
            } catch (error) {
              console.error(`Failed to create string for field ${field.id}[${valueIndex}]:`, error);
              translatableValues.push({ text: textValue, string_id: undefined });
            }
          } else {
            translatableValues.push({ text: textValue, string_id: existingStringId });
          }
        }
        
        // Update field value with TranslatableValue array
        updatedData[sectionIndex].fields[fieldIndex].value = translatableValues;
        fieldsToCreateStrings.push(field.id);
        
      } else if (typeof field.value === 'string') {
        // Handle single translatable field (legacy format)
        // Process even if empty to establish translation link
        
        // Check if we need to create a new string or update an existing one
        if (field.string_id && updateStringFn && !fieldsToCreateStrings.includes(field.id)) {
          // Try to update existing string (only if we're not creating a new one for this field)
          try {
            console.log(`Attempting to update existing string ${field.string_id} for field ${field.id}`);
            await updateStringFn({
              id: field.string_id,
              game_id: glossaryItem.game_id,
              source_text: field.value
            });
            updatedStrings.push({ id: field.string_id, source_text: field.value });
            console.log(`Successfully updated string ${field.string_id} for field ${field.id}`);
            continue; // Skip to next field since we successfully updated
          } catch (error) {
            console.error(`Failed to update string for field ${field.id}:`, error);
            // If update fails, clear the string_id and create a new one
            updatedData[sectionIndex].fields[fieldIndex].string_id = undefined;
            field.string_id = undefined;
            console.log(`Cleared string_id for field ${field.id}, will create new string`);
          }
        }
        
        // Create new string if we don't have a string_id or update failed
        // Create even if empty to establish the translation link
        if (!field.string_id) {
          try {
            console.log(`Creating new string for field ${field.id} with value: "${field.value}"`);
            
            // Generate LocaKey
            const locaKey = generateGlossaryLocaKey(glossaryItem.name, field.id);

            // Create string metadata
            const metadata = [
              { key: 'glossary_item_id', value: glossaryItem.id || 'pending' },
              { key: 'field_id', value: field.id },
              { key: 'field_label', value: field.label },
              { key: 'glossary_title', value: glossaryItem.name },
              { key: 'source', value: 'glossary_item' }
            ];

            // Create the string (even if empty to establish link)
            const newString = await createStringFn({
              game_id: glossaryItem.game_id,
              source_text: field.value,
              section: 'glossary',
              context: `Glossary item: ${glossaryItem.name} - Field: ${field.label}`,
              metadata,
              abbrev: locaKey,
              status: 'pending'
            });
            
            // Store the string ID in the field
            updatedData[sectionIndex].fields[fieldIndex].string_id = newString.id;
            createdStrings.push(newString);
            fieldsToCreateStrings.push(field.id); // Track this field as getting a new string
            console.log(`Successfully created string ${newString.id} for field ${field.id}`);
          } catch (error) {
            console.error(`Failed to create string for field ${field.id}:`, error);
          }
        }
      }
    }
  }
  
  console.log(`Finished processing glossary item. Created ${createdStrings.length} strings, updated ${updatedStrings.length} strings`);
  
  const processedItem = { 
    ...glossaryItem, 
    data: updatedData,
    // Explicitly preserve identifiers to ensure they're not lost
    identifiers: glossaryItem.identifiers || []
  };
  
  console.log("DEBUG - processObjectForTranslation output identifiers:", processedItem.identifiers);
  
  return {
    glossaryItem: processedItem,
    createdStrings,
    updatedStrings
  };
}
